import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';
import { getBenefitIcon, BenefitIconType } from '../../components/shared/BenefitIcons';
import useScrollToTop from '../../hooks/useScrollToTop';

const EB1ANIW: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  useScrollToTop();

  const benefits: { icon: BenefitIconType; text: string }[] = [
    { icon: 'check', text: t('us.eb1a.benefits.noLaborCert') },
    { icon: 'work', text: t('us.eb1a.benefits.noJobOffer') },
    { icon: 'timeline', text: t('us.eb1a.benefits.priorityProcessing') },
    { icon: 'family', text: t('us.eb1a.benefits.familyGreenCard') },
    { icon: 'security', text: t('us.eb1a.benefits.workAuth') },
    { icon: 'global', text: t('us.eb1a.benefits.citizenshipPath') },
  ];

  const requirements = [
    t('us.eb1a.requirements.extraordinaryAbility'),
    t('us.eb1a.requirements.nationalInterest'),
    t('us.eb1a.requirements.achievements'),
    t('us.eb1a.requirements.criminalRecord'),
    t('us.eb1a.requirements.passport'),
    t('us.eb1a.requirements.processingFee'),
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))',
          backgroundColor: '#1976d2',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '40vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" component="h1" gutterBottom>
              {t('us.eb1a.hero.title')}
            </Typography>
            <Typography variant="h5" gutterBottom>
              {t('us.eb1a.hero.subtitle')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Program Overview */}
      <Container sx={{ py: 8 }}>
        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} md={8}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography variant="h4" gutterBottom align="center">
                {t('us.eb1a.overview.title')}
              </Typography>
              <Typography variant="body1" paragraph align="center">
                {t('us.eb1a.overview.description1')}
              </Typography>
              <Typography variant="body1" paragraph align="center">
                {t('us.eb1a.overview.description2')}
              </Typography>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            {t('us.eb1a.benefits.title')}
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ color: 'primary.main', mr: 2 }}>
                          {getBenefitIcon(benefit.icon)}
                        </Box>
                        <Typography variant="h6" component="h3">
                          {benefit.text}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Requirements Section */}
      <Container sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          {t('us.eb1a.requirements.title')}
        </Typography>
        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {t('us.eb1a.requirements.title')}
                  </Typography>
                  <List>
                    {requirements.map((requirement, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={requirement} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {t('us.eb1a.timeline.title')}
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary={t('us.eb1a.timeline.initial.title')}
                        secondary={t('us.eb1a.timeline.initial.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={t('us.eb1a.timeline.i140.title')}
                        secondary={t('us.eb1a.timeline.i140.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={t('us.eb1a.timeline.greenCard.title')}
                        secondary={t('us.eb1a.timeline.greenCard.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={t('us.eb1a.timeline.citizenship.title')}
                        secondary={t('us.eb1a.timeline.citizenship.description')}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default EB1ANIW; 